import React, { useState, useEffect, useRef } from "react";
import { IonButton, IonContent, IonInput, IonItem, IonLabel, IonModal, IonPage } from "@ionic/react";
import { Helmet } from "react-helmet";

const SubstractionWordProblem: React.FC = () => {
    const [num1, setNum1] = useState<number>(0);
    const [num2, setNum2] = useState<number>(0);
    const [correctAnswer, setCorrectAnswer] = useState<number>(0);
    const [userAnswer, setUserAnswer] = useState<string>("");
    const [message, setMessage] = useState<string>("");
    const modal = useRef<HTMLIonModalElement>(null);

    const wordProblems = [
        "You have {num1} apples. You give away {num2} apples. How many are left?",
        "There were {num1} balloons at the party. {num2} popped. How many remain?",
        "A farmer has {num1} cows. He sells {num2}. How many does he have now?",
        "There were {num1} candies in the jar. {num2} were eaten. How many are left?",
        "You had {num1} toy cars. You lost {num2}. How many do you still have?",
        "A basket had {num1} oranges. {num2} were taken out. How many are left?",
        "You have {num1} pencils in your box. You gave {num2} to your friend. How many are left?",
        "There are {num1} chocolates in the fridge. {num2} melted. How many remain?",
        "A bakery had {num1} cupcakes. {num2} were sold. How many are left?",
        "There were {num1} ducks in the pond. {num2} swam away. How many are left?",
        "A school has {num1} chairs. {num2} broke. How many are still usable?",
        "Your toy train set had {num1} tracks. {num2} got lost. How many do you have now?",
        "There were {num1} fish in the tank. {num2} were moved to another tank. How many are left?",
        "A farmer had {num1} sheep. {num2} wandered off. How many are left?",
        "You started with {num1} crayons. {num2} broke. How many do you still have?",
        "A store had {num1} hats. {num2} were sold. How many are remaining?",
        "You had {num1} stickers in your book. {num2} fell out. How many do you have now?",
        "A farmer planted {num1} trees. {num2} were cut down. How many trees are still standing?",
        "You had {num1} marbles. {num2} rolled away. How many are left?",
        "A school bus started with {num1} students. {num2} got off at the first stop. How many are still on the bus?",
        "There were {num1} pizza slices. {num2} were eaten. How many are left?",
        "You collected {num1} seashells. {num2} broke. How many do you have now?",
        "A gardener planted {num1} flowers. {num2} dried up. How many are left?",
        "There were {num1} books on the shelf. {num2} were borrowed. How many remain?",
        "A soccer team had {num1} players. {num2} got injured. How many can still play?",
        "You had {num1} Legos. {num2} went missing. How many do you still have?",
        "There were {num1} muffins in the tray. {num2} were eaten. How many are left?",
        "A pond had {num1} frogs. {num2} jumped away. How many are still there?",
        "You started with {num1} pieces of candy. {num2} were given to your friend. How many do you have now?",
        "A zoo had {num1} monkeys. {num2} were moved to another zoo. How many remain?",
        "A grocery store had {num1} milk cartons. {num2} were sold. How many are left?",
        "There were {num1} soccer balls. {num2} got lost. How many are left?",
        "Your art set had {num1} colored markers. {num2} dried out. How many can still be used?",
        "You had {num1} notebooks. {num2} were used up. How many are left?",
        "A farm had {num1} hens. {num2} were sold. How many hens are still there?",
        "There were {num1} spoons in the kitchen. {num2} were taken out. How many remain?",
        "A bicycle shop had {num1} bikes. {num2} were sold. How many are left?",
        "A family baked {num1} cookies. {num2} were eaten. How many are left?",
        "You started with {num1} paper planes. {num2} got crumpled. How many are left?",
        "A factory produced {num1} toy cars. {num2} were damaged. How many are in good condition?",
        "Your basket had {num1} bananas. {num2} were eaten. How many remain?",
        "A basketball team had {num1} players. {num2} got sick. How many can still play?",
        "There were {num1} turtles in the pond. {num2} swam away. How many are still there?",
        "A spaceship carried {num1} astronauts. {num2} went on a spacewalk. How many are inside?",
        "You had {num1} candles. {num2} melted. How many are still in shape?",
        "A magician had {num1} magic wands. {num2} disappeared. How many does he have left?",
        "You collected {num1} colorful buttons. {num2} fell on the floor. How many are left?",
        "A zoo had {num1} penguins. {num2} were moved to another enclosure. How many are still there?",
        "There were {num1} toy dinosaurs. {num2} got lost. How many are left?",
        "A spaceship had {num1} alien passengers. {num2} landed on Earth. How many are still aboard?",
        "Your treasure chest had {num1} golden coins. {num2} were spent. How many are left?"
    ];


    const getRandomNumber = (min: number, max: number) => Math.floor(Math.random() * (max - min + 1)) + min;

    const generateQuestion = () => {
        const num1 = getRandomNumber(5, 9);
        const num2 = getRandomNumber(1, num1 - 1);
        const questionTemplate = wordProblems[Math.floor(Math.random() * wordProblems.length)];
        const question = questionTemplate.replace("{num1}", num1.toString()).replace("{num2}", num2.toString());
        const correctAnswer = num1 - num2;

        const options = new Set<number>();
        options.add(correctAnswer);
        while (options.size < 3) {
            options.add(getRandomNumber(0, 9));
        }

        return {
            question,
            correctAnswer,
            options: Array.from(options).sort(() => Math.random() - 0.5) // Shuffle options
        };
    };

    const [quiz, setQuiz] = useState(generateQuestion());
    const [feedback, setFeedback] = useState("");
    const [showImage, setShowImage] = useState(false);
    document.title = "Subtraction Word Problems for UKG | Easy Real-Life Math Questions";

    const handleAnswerClick = (option: number) => {
        if (option === quiz.correctAnswer) {
            setShowImage(true);
            setTimeout(() => {
                generateQuestion()
                setShowImage(false);
                setMessage("")
            }, 5000)
        } else {
            setShowImage(false);
            setMessage("❌ Oops! Try again.");
        }
    };

    return (
        <>
            <Helmet>
                <meta name="description" content="Teach subtraction to UKG kids using simple word problems based on real-life situations. Download worksheets and improve early math thinking skills." />
                <link rel="canonical" href="https://mamasguidance.com/ukg/mathematics/substraction/word-problem" />
            </Helmet>
            {
                showImage === false ?
                    <>
                        <div className="contentPart additionWord pickColor matchImage match-container" style={{ textAlign: "center", padding: "20px" }}>
                            <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                            <IonModal
                                ref={modal}
                                trigger="open-modal"
                                initialBreakpoint={1}
                                breakpoints={[0, 0.5, 0.75, 1]}
                                handleBehavior="cycle"
                                backdropDismiss={false}  // ✅ Prevents click-to-close
                                className="full-page-modal"
                            >
                                <IonContent className="ion-padding">
                                    <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                    <h1>Subtraction Word Problems</h1>

                                    <p>
                                        Help your child <strong>apply subtraction in real-life</strong> with these fun and easy word problems! Perfect for UKG students, these subtraction story problems use everyday situations to build logic, thinking, and math confidence. Word problems encourage kids to read, understand, and solve in steps.
                                    </p>

                                    <h2>🎯 Why Word Problems Are Important</h2>
                                    <ul>
                                        <li>Promotes real-world thinking</li>
                                        <li>Improves reading and comprehension skills</li>
                                        <li>Strengthens concept of subtraction as <strong>“taking away”</strong></li>
                                        <li>Builds logical problem-solving abilities</li>
                                    </ul>

                                    <h2>🧠 What Will Your Child Learn?</h2>
                                    <ul>
                                        <li>Identify the subtraction situation from a story</li>
                                        <li>Find numbers involved in the question</li>
                                        <li>Understand and write subtraction equations</li>
                                        <li>Solve step-by-step with guidance</li>
                                    </ul>

                                    <h2>📚 Sample Subtraction Word Problems</h2>
                                    <ul>
                                        <li><strong>Problem 1:</strong> Riya had 5 apples. She gave 2 to her friend. How many apples are left?<br />👉 <em>5 − 2 = 3</em></li>
                                        <li><strong>Problem 2:</strong> There were 8 balloons. 3 flew away. How many balloons are still there?<br />👉 <em>8 − 3 = 5</em></li>
                                        <li><strong>Problem 3:</strong> A box had 10 chocolates. Rahul ate 4. How many are left?<br />👉 <em>10 − 4 = 6</em></li>
                                    </ul>



                                    <h2>🔗 Related Topics</h2>
                                    <div>
                                        <IonItem routerLink="/ukg/mathematics/smallest-number"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Smallest Number</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/ukg/mathematics/biggest-number"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Biggest Number</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/ukg/mathematics/number-comparison"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Number Comparison</IonLabel>
                                        </IonItem>
                                    </div>

                                    <h2>🏡 Home Teaching Tips</h2>
                                    <ul>
                                        <li>Create short stories using toys and snacks (<strong>e.g., “You had 7 cookies, ate 2...”</strong>)</li>
                                        <li>Ask your child to draw what’s happening in the story</li>
                                        <li>Use real-world situations like shopping, sharing, or playing</li>
                                        <li>Practice with daily routine (e.g., <strong>“You had 4 crayons, lost 1...”</strong>)</li>
                                    </ul>

                                    <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                                </IonContent>
                            </IonModal>
                            <article className="visually-hidden">
                                <h1>Subtraction Word Problems</h1>

                                <p>
                                    Help your child <strong>apply subtraction in real-life</strong> with these fun and easy word problems! Perfect for UKG students, these subtraction story problems use everyday situations to build logic, thinking, and math confidence. Word problems encourage kids to read, understand, and solve in steps.
                                </p>

                                <h2>🎯 Why Word Problems Are Important</h2>
                                <ul>
                                    <li>Promotes real-world thinking</li>
                                    <li>Improves reading and comprehension skills</li>
                                    <li>Strengthens concept of subtraction as <strong>“taking away”</strong></li>
                                    <li>Builds logical problem-solving abilities</li>
                                </ul>

                                <h2>🧠 What Will Your Child Learn?</h2>
                                <ul>
                                    <li>Identify the subtraction situation from a story</li>
                                    <li>Find numbers involved in the question</li>
                                    <li>Understand and write subtraction equations</li>
                                    <li>Solve step-by-step with guidance</li>
                                </ul>

                                <h2>📚 Sample Subtraction Word Problems</h2>
                                <ul>
                                    <li><strong>Problem 1:</strong> Riya had 5 apples. She gave 2 to her friend. How many apples are left?<br />👉 <em>5 − 2 = 3</em></li>
                                    <li><strong>Problem 2:</strong> There were 8 balloons. 3 flew away. How many balloons are still there?<br />👉 <em>8 − 3 = 5</em></li>
                                    <li><strong>Problem 3:</strong> A box had 10 chocolates. Rahul ate 4. How many are left?<br />👉 <em>10 − 4 = 6</em></li>
                                </ul>



                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/ukg/mathematics/smallest-number"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Smallest Number</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/mathematics/biggest-number"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Biggest Number</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/mathematics/number-comparison"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Number Comparison</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🏡 Home Teaching Tips</h2>
                                <ul>
                                    <li>Create short stories using toys and snacks (<strong>e.g., “You had 7 cookies, ate 2...”</strong>)</li>
                                    <li>Ask your child to draw what’s happening in the story</li>
                                    <li>Use real-world situations like shopping, sharing, or playing</li>
                                    <li>Practice with daily routine (e.g., <strong>“You had 4 crayons, lost 1...”</strong>)</li>
                                </ul>
                            </article>
                            <h2>{quiz.question}</h2>
                            <div style={{ marginBottom: "20px", display: 'flex', flexWrap: 'wrap' }}>
                                {quiz.options.map((option, index) => (
                                    <div className="background-blue" key={index} onClick={() => handleAnswerClick(option)} style={{ margin: "5px", padding: "10px", width: "60px" }}>
                                        {option}
                                    </div>
                                ))}
                            </div>
                            {feedback && <p>{feedback}</p>}
                        </div>
                    </>
                    :
                    <>
                        <div className="contentPart showMiddle">
                            <img style={{ height: '150px' }} className='img-fluid' src={process.env.REACT_APP_WELDONE_LINK} />
                        </div>
                    </>
            }
        </>
    );
};

export default SubstractionWordProblem;
